// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * Override this handler to parse product videos
 */
import { addToChainAfter, replaceInChain } from "@insite/client-framework/HandlerCreator";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    chain,
    DispatchCompleteLoadProduct,
    PopulateApiParameter,
} from "@insite/client-framework/Store/Data/Products/Handlers/LoadProductByPath";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import setCustomProductState from "@stanion/App/Handlers/ProductDetails/SetCustomProductState";
import { VideoDocumentModel } from "@stanion/App/Store/Reducers/xProductDetailsReducer";
import { getSettingsCollection } from "@stanion/App/Store/Selectors/SettingsSelectors";
import { getYoutubeThumbnail } from "@stanion/XDL/Utilities/helpers";

export const CustomPopulateApiParameter: typeof PopulateApiParameter = props => {
    if (props.apiParameter.expand) {
        props.apiParameter.expand.push("properties");
    } else {
        props.apiParameter.expand = ["properties"];
    }
};

function getVideosList(product: ProductModel) {
    return product.properties.webVideos ? JSON.parse(product.properties.webVideos) : [];
}

function getProductVideoThumbPath(video: any, defaultVideoThumbnailImage: string) {
    const isYoutubeVideo = JSON.parse(video.isYoutubeVideo);
    return isYoutubeVideo ? getYoutubeThumbnail(video.videoLink as string) : defaultVideoThumbnailImage;
}
function parseProductVideos(product: ProductModel, state: ApplicationState): VideoDocumentModel[] {
    const documentVideos = getVideosList(product);

    const settingsCollection = getSettingsCollection(state);
    const defaultVideoThumbnailImage = settingsCollection.customSettings.videoThumbnailImage ||
        settingsCollection.catalogGeneralSettings.notFoundSmallImagePath ||
        settingsCollection.catalogGeneralSettings.notFoundMediumImagePath || "";
    const videos: VideoDocumentModel[] = documentVideos.map((video: any) => {
        return {
            id: video.id,
            link: video.videoLink,
            isYoutubeVideo: JSON.parse(video.isYoutubeVideo),
            filename: video.filename,
            thumbnail: getProductVideoThumbPath(video, defaultVideoThumbnailImage),
        };
    });

    return videos;
}

export function parseProductCustomState(product: ProductModel, state: ApplicationState) {
    const videos = parseProductVideos(product, state);
    let selectedVideoIndex = -1;
    if (videos && videos.length > 0 && product?.images?.length === 0) {
        selectedVideoIndex = 0;
    }

    return {
        productId: product.id,
        videos,
        selectedVideoIndex,
    };
}

export const CustomDispatchCompleteLoadProduct: typeof DispatchCompleteLoadProduct = props => {
    if (!props.apiResult) {
        return;
    }
    const state = props.getState();
    props.dispatch(setCustomProductState({ customProductStates: [parseProductCustomState(props.apiResult, state)] }));

    return DispatchCompleteLoadProduct(props);
};

addToChainAfter(chain, PopulateApiParameter, CustomPopulateApiParameter);
replaceInChain(chain, DispatchCompleteLoadProduct, CustomDispatchCompleteLoadProduct);
